import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LucideAngularModule } from "lucide-angular";
import { Subscription } from "rxjs";
import {
  CustomModalService,
  ModalState,
} from "../../../services/custom-modal.service";

@Component({
  selector: "app-global-modal",
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  template: `
    <div
      class="custom-modal-overlay"
      *ngIf="modalState.isVisible"
      (click)="onOverlayClick()"
    >
      <div class="custom-modal" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <div class="modal-icon" *ngIf="modalState.config?.icon">
            <lucide-icon
              [name]="modalState.config?.icon"
              [size]="24"
            ></lucide-icon>
          </div>
          <div
            class="modal-icon"
            *ngIf="
              !modalState.config?.icon && modalState.config?.type === 'confirm'
            "
          >
            <img
              src="assets/reball/reball logo no bg.png"
              alt="ReBall"
              style="width:32px;height:32px;object-fit:contain;border-radius:8px;"
            />
          </div>
          <div
            class="modal-icon"
            *ngIf="
              !modalState.config?.icon && modalState.config?.type === 'info'
            "
          >
            <lucide-icon name="info" [size]="24"></lucide-icon>
          </div>
          <div
            class="modal-icon"
            *ngIf="
              !modalState.config?.icon && modalState.config?.type === 'warning'
            "
          >
            <lucide-icon name="alert-triangle" [size]="24"></lucide-icon>
          </div>
          <div
            class="modal-icon"
            *ngIf="
              !modalState.config?.icon && modalState.config?.type === 'error'
            "
          >
            <lucide-icon name="circle-x" [size]="24"></lucide-icon>
          </div>
          <h3 class="modal-title">{{ modalState.config?.title }}</h3>
        </div>

        <div class="modal-content">
          <p class="modal-message">{{ modalState.config?.message }}</p>
        </div>

        <div class="modal-actions">
          <button
            *ngIf="modalState.onCancel"
            class="modal-btn cancel"
            (click)="onCancel()"
          >
            <span class="btn-icon">✕</span>
            <span class="btn-text">{{
              modalState.config?.cancelText || "Cancel"
            }}</span>
          </button>
          <button class="modal-btn confirm" (click)="onConfirm()">
            <span class="btn-icon">✓</span>
            <span class="btn-text">{{
              modalState.config?.confirmText || "OK"
            }}</span>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .custom-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        backdrop-filter: blur(4px);
      }

      .custom-modal {
        background: white;
        border-radius: 20px;
        max-width: 400px;
        width: 90vw;
        overflow: hidden;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        animation: modalSlideIn 0.3s ease-out;
      }

      @keyframes modalSlideIn {
        from {
          opacity: 0;
          transform: translateY(-20px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      .modal-header {
        background: var(--rb-primary);
        color: white;
        padding: 24px;
        text-align: center;
      }

      .modal-icon {
        font-size: 48px;
        margin-bottom: 12px;
      }

      .modal-title {
        font-size: 18px;
        font-weight: 700;
        margin: 0;
      }

      .modal-content {
        padding: 24px;
        text-align: center;
      }

      .modal-message {
        font-size: 16px;
        color: #374151;
        line-height: 1.5;
        margin: 0;
      }

      .modal-actions {
        display: grid;
        grid-template-columns: 1fr 1fr;
        border-top: 1px solid var(--rb-surface-alt);
      }

      .modal-actions.single {
        grid-template-columns: 1fr;
      }

      .modal-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 16px;
        border: none;
        background: white;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 14px;
        font-weight: 600;
      }

      .modal-btn:first-child:not(:only-child) {
        border-right: 1px solid #e2e8f0;
      }

      .modal-btn.cancel {
        color: #64748b;
      }

      .modal-btn.cancel:hover {
        background: #f8fafc;
        color: #374151;
      }

      .modal-btn.confirm {
        color: var(--rb-teal);
      }

      .modal-btn.confirm:hover {
        background: #e0f5f3;
        color: #152238;
      }

      .btn-icon {
        font-size: 14px;
      }

      .btn-text {
        font-size: 14px;
      }
    `,
  ],
})
export class GlobalModalComponent implements OnInit, OnDestroy {
  modalState: ModalState = { isVisible: false, config: null };
  private subscription?: Subscription;

  constructor(private modalService: CustomModalService) {}

  ngOnInit() {
    this.subscription = this.modalService.modalState$.subscribe(
      (state) => (this.modalState = state),
    );
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  onOverlayClick() {
    if (this.modalState.onCancel) {
      this.onCancel();
    } else {
      this.onConfirm();
    }
  }

  onConfirm() {
    this.modalState.onConfirm?.();
  }

  onCancel() {
    this.modalState.onCancel?.();
  }
}
